/**
 * Applies a single AI improvement suggestion to a script graph.
 * Returns a NEW graph object (does not mutate the original).
 *
 * @param {object} graph - The template graph { nodes: {...}, ui: {...} }
 * @param {object} suggestion - A suggestion from generateScriptImprovements
 * @returns {{ graph: object, applied: boolean, error?: string }}
 */
export function applyImprovementToGraph(graph, suggestion) {
  if (!graph?.nodes || !suggestion) {
    return { graph, applied: false, error: 'Invalid graph or suggestion' };
  }

  const nodes = { ...graph.nodes };
  const nodeId = suggestion.targetNodeId;
  const type = suggestion.type;

  if (type !== 'add_response' && type !== 'add_objection' && !nodes[nodeId]) {
    return { graph, applied: false, error: `Node "${nodeId}" not found in graph` };
  }

  switch (type) {
    case 'rewrite_script': {
      if (!suggestion.suggested) {
        return { graph, applied: false, error: 'No suggested text provided' };
      }
      nodes[nodeId] = {
        ...nodes[nodeId],
        script: suggestion.suggested,
      };
      break;
    }

    case 'update_coaching': {
      const newCoaching = suggestion.suggestedCoaching || suggestion.suggested;
      if (!newCoaching) {
        return { graph, applied: false, error: 'No suggested coaching text provided' };
      }
      nodes[nodeId] = {
        ...nodes[nodeId],
        coaching: newCoaching,
      };
      break;
    }

    case 'add_response': {
      if (!nodes[nodeId]) {
        return { graph, applied: false, error: `Node "${nodeId}" not found in graph` };
      }
      const newResp = suggestion.newResponse;
      if (!newResp?.label) {
        return { graph, applied: false, error: 'No response label provided' };
      }

      const existingResponses = nodes[nodeId].responses || {};
      const respKey = newResp.label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/(^_|_$)/g, '');

      const nextNode = newResp.suggestedNextNode || newResp.nextNode || '__end_script__';

      nodes[nodeId] = {
        ...nodes[nodeId],
        responses: {
          ...existingResponses,
          [respKey]: {
            label: newResp.label,
            nextNode,
            data: {},
          },
        },
      };
      break;
    }

    case 'add_objection': {
      if (!nodes[nodeId]) {
        return { graph, applied: false, error: `Node "${nodeId}" not found in graph` };
      }
      const objections = [...(nodes[nodeId].objectionResponses || [])];
      objections.push({
        label: suggestion.suggested || suggestion.newResponse?.label || 'New objection',
        response: suggestion.suggestedCoaching || suggestion.newResponse?.response || '',
        coaching: suggestion.impact || '',
      });
      nodes[nodeId] = {
        ...nodes[nodeId],
        objectionResponses: objections,
      };
      break;
    }

    case 'restructure': {
      const action = suggestion.restructureAction;
      if (!action || action.action !== 'move_after' || !action.moveAfterNodeId) {
        return { graph, applied: false, error: 'Restructure suggestion missing moveAfterNodeId' };
      }
      if (!nodes[action.moveAfterNodeId]) {
        return { graph, applied: false, error: `Target position node "${action.moveAfterNodeId}" not found` };
      }
      if (action.moveAfterNodeId === nodeId) {
        return { graph, applied: false, error: 'Cannot move a node after itself' };
      }

      // Capture the insert point's current successor BEFORE detaching
      const insertSuccessorBeforeDetach = getFirstSuccessor(nodes[action.moveAfterNodeId]);
      const targetSuccessor = getFirstSuccessor(nodes[nodeId]);

      // 1. Detach: rewire all predecessors to skip the moved node
      for (const nid of Object.keys(nodes)) {
        if (nid === nodeId) continue;
        nodes[nid] = rewireResponses(nodes[nid], nodeId, targetSuccessor);
      }

      // 2. Determine where to point the moved node's responses.
      //    If insert-after was a direct predecessor, its successor is now
      //    targetSuccessor (from step 1). Use the pre-detach snapshot.
      const actualInsertSuccessor = insertSuccessorBeforeDetach === nodeId
        ? targetSuccessor
        : insertSuccessorBeforeDetach;

      // 3. Insert: insertAfterNode → moved node → old successor
      nodes[action.moveAfterNodeId] = rewireResponses(
        nodes[action.moveAfterNodeId],
        getFirstSuccessor(nodes[action.moveAfterNodeId]),
        nodeId,
        true,
      );
      nodes[nodeId] = rewireAllSuccessors(nodes[nodeId], actualInsertSuccessor);

      break;
    }

    case 'remove_node': {
      const removedNode = nodes[nodeId];
      const removedSuccessor = getFirstSuccessor(removedNode);

      // Rewire all predecessors to skip this node
      for (const nid of Object.keys(nodes)) {
        if (nid === nodeId) continue;
        nodes[nid] = rewireResponses(nodes[nid], nodeId, removedSuccessor);
      }

      delete nodes[nodeId];
      break;
    }

    default:
      return { graph, applied: false, error: `Unknown suggestion type: ${type}` };
  }

  return {
    graph: { ...graph, nodes },
    applied: true,
  };
}

/**
 * Get the first successor nodeId from a node's responses.
 * Returns '__end_script__' if no responses exist.
 */
function getFirstSuccessor(node) {
  const responses = node?.responses || {};
  const firstResp = Object.values(responses)[0];
  return firstResp?.nextNode || '__end_script__';
}

/**
 * Rewire a node's responses: replace references to `fromNodeId` with `toNodeId`.
 * If `replaceFirst` is true and fromNodeId isn't found, replace the first response's nextNode.
 */
function rewireResponses(node, fromNodeId, toNodeId, replaceFirst = false) {
  const responses = node.responses || {};
  let changed = false;
  const newResponses = {};

  for (const [key, resp] of Object.entries(responses)) {
    if (resp.nextNode === fromNodeId) {
      newResponses[key] = { ...resp, nextNode: toNodeId };
      changed = true;
    } else {
      newResponses[key] = resp;
    }
  }

  if (!changed && replaceFirst) {
    const keys = Object.keys(newResponses);
    if (keys.length > 0) {
      const firstKey = keys[0];
      newResponses[firstKey] = { ...newResponses[firstKey], nextNode: toNodeId };
    }
  }

  return { ...node, responses: newResponses };
}

/**
 * Point all of a node's responses to the given successor.
 */
function rewireAllSuccessors(node, newSuccessor) {
  const responses = node.responses || {};
  const newResponses = {};
  for (const [key, resp] of Object.entries(responses)) {
    newResponses[key] = { ...resp, nextNode: newSuccessor };
  }
  return { ...node, responses: newResponses };
}

/**
 * Apply multiple suggestions to a graph in sequence.
 * Returns the final graph plus a list of results per suggestion.
 */
export function applyMultipleImprovements(graph, suggestions) {
  let currentGraph = graph;
  const results = [];

  for (const suggestion of suggestions) {
    const result = applyImprovementToGraph(currentGraph, suggestion);
    results.push({ id: suggestion.id, ...result });
    if (result.applied) {
      currentGraph = result.graph;
    }
  }

  return { graph: currentGraph, results };
}
